import React, { memo, useContext, useEffect, useState } from "react";
import { getProfessionalInfo } from "../../../API/auth";
import { updatePortfolio } from "../../../API/profile";
import Button from "../../../components/Button";
import DocumentCard from "./Document";
import { Image } from "../../../components/CustomeImage";
import { StoreContext } from "../../../context";
import { uploadToAws } from "../../../helpers/uploader";
import useFetch from "../../../hooks/useFetch";
import { Portfolio } from "../../../react-app-env";
import { flexer, hoverFade } from "../../../styles/globalStyles";
import { FormProps } from "./Form1";
import { File } from "../../ContractorForm/ContractorForm";
import randomColor from "randomcolor";

const getRandomColors = (count: number) =>
  randomColor({ count, luminosity: "dark" });

const Form2 = ({ showModal, toggleModal }: FormProps<Portfolio>) => {
  const [docs, setDocs] = useState<{
    [key: string]: { file: any; id: number; projectId?: string };
  }>({});
  const { user, handleContext } = useContext(StoreContext);
  const [hasFormChanges, setFormChanges] = useState(false);
  const { success, isLoading, setLoader, load, setSuccess } = useFetch();
  const [current, setCurrent] = useState<Portfolio & { color: string }>();
  const [projects, setProjects] = useState<Portfolio[]>([]);
  const [colors, setColors] = useState<string[]>([]);

  useEffect(() => {
    if (!projects[0] && showModal.missing) {
      setProjects(showModal.missing.projects);
      setColors(getRandomColors(showModal.missing.projects.length));
    } else if (current) {
      setProjects((prev) => {
        return prev.map((one) => {
          if (one._id === current._id) {
            return current;
          } else {
            return one;
          }
        });
      });
    }
    // eslint-disable-next-line
  }, [user, showModal]);

  useEffect(() => {
    if (!current && success) setSuccess(false);
  }, [current, success, setSuccess]);

  const handleSubmit = (data: Portfolio) => {
    /** Databse Update APIs */
    if (current)
      load(updatePortfolio(current._id, data))
        .then(() =>
          getProfessionalInfo().then((res) => {
            handleContext("user", res.data);
          })
        )
        .finally(() => setLoader(false));
  };

  const handleDocs = (
    key: string,
    file: any,
    id: number,
    projectId?: string
  ) => {
    if (success) setSuccess((prev) => !prev);
    // update changes state
    if (!hasFormChanges) setFormChanges(true);
    //
    setDocs((prev) => ({ ...prev, [key]: { file, id, projectId } }));
  };

  const submitPlant = async () => {
    /** Prepare files */
    setLoader(true);

    let files: { [key: string]: any } = {};
    const images: File[] = [];

    /** Uploading Files */
    if (showModal.missing?.fields)
      for (const { key, projectId } of showModal.missing.fields) {
        if (!docs[key + projectId]) continue;

        if (current?._id !== projectId) continue;

        if (key === "projectImages") {
          const files = docs[key + projectId].file;
          for (const file of files) {
            /** check if the file has a key and jump it */
            if (file.key) {
              images.push(file);
              continue;
            }

            const imageKey = await uploadToAws(file);
            if (!imageKey) {
              // if value key is null
              // hasAnError = true;
              setLoader(false);
              return true;
            }
            images.push({
              key: imageKey,
              meta: {
                size: file.size,
                name: file.name,
                type: file.type,
              },
            });
          }
        } else {
          const file = docs[key + projectId].file;
          const imageKey = await uploadToAws(file);
          if (!imageKey) {
            // if value key is null
            // hasAnError = true;
            setLoader(false);
            return true;
          }
          files[key] = imageKey;
        }
      }

    if (!user) return null;

    const data: any = {
      ...current,
      ...files,
    };

    if (images[0]) data["projectImages"] = images;

    delete data.__v;
    delete data._id;
    delete data.professional;
    delete data.color;

    setCurrent((prev) => ({ ...prev, ...data }));
    handleSubmit(data);
  };

  const onDelete = (docKey: keyof Portfolio) => {
    const data = { ...current };

    delete data._id;
    delete data.__v;
    delete data.color;
    delete data.professional;

    data[docKey] = "" as any;

    return updatePortfolio(current?._id || "", data);
  };

  const Fields = (
    <>
      {current
        ? React.Children.toArray(
            showModal.missing &&
              showModal.missing?.fields.map(
                ({ accept, multiple, key, initialValue, label, projectId }) => {
                  if (projectId !== current._id) return null;
                  return (
                    <DocumentCard
                      handleChange={(file) =>
                        handleDocs(
                          key + projectId,
                          file,
                          showModal.missing?.id || 0,
                          projectId
                        )
                      }
                      onDelete={() => onDelete(key)}
                      handleDelete={(doc: any) => {
                        if (multiple) {
                          const query = doc.key || doc.name;
                          const data =
                            docs[key + projectId]?.file || initialValue;
                          const newInitialValue = data.filter((one: any) =>
                            one.key ? one.key !== query : one.name !== query
                          );
                          //
                          handleDocs(
                            key + projectId,
                            newInitialValue,
                            showModal.missing?.id || 0,
                            projectId
                          );
                        }
                      }}
                      value={docs[key + projectId]?.file || initialValue}
                      {...{ label, key, accept, multiple }}
                    />
                  );
                }
              )
          )
        : null}
      <div className={flexer}>
        <Button
          type="transparent"
          className="mr-2 w-1/2"
          text={success ? "Back" : "Cancel"}
          onClick={() => setCurrent(undefined)}
        />
        <Button
          text="Submit"
          className="w-1/2"
          onClick={submitPlant}
          {...{ isLoading, success }}
          type={hasFormChanges ? "primary" : "muted"}
        />
      </div>
    </>
  );

  const Projects = (
    <>
      <p className="text-LIGHT_TEXT text-base font-medium mb-5">
        Upload documents associated with the following projects.
      </p>
      <div className="flex overflow-x-scroll">
        {React.Children.toArray(
          projects.map((one, index) => (
            <div
              onClick={() => setCurrent({ ...one, color: colors[index] })}
              className="bg-LIGHT_BG rounded-md overflow-hidden w-44 h-full mb-5 mr-6 cursor-pointer"
            >
              <Image
                divClassName="w-full p-0"
                // imgStyles={{background:colors[index]}}
                src={one.projectImages[0] && one.projectImages[0].key}
                className={`${
                  !one.projectImages[0] ? "p-5" : "p-0"
                } w-full h-40 object-cover`}
              />
              <p className="font-medium text-base p-4 truncate capitalize">
                {one.title}
              </p>
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <>
      <div className={flexer + "mb-2"}>
        <strong className="capitalize">
          {current?.title || showModal.missing?.label}
        </strong>
        <button
          onClick={() => toggleModal()}
          className={"text-MUTED_TEXT text-base" + hoverFade}
        >
          Close
        </button>
      </div>
      {current ? Fields : Projects}
    </>
  );
};

export default memo(Form2);
